import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CommonDataParamsService } from './common-data-params.service';
import { EventsService } from '../services/data-api/events.service';
import {
  CollapseOnOption,
  EventTemporality,
  EventFactuality,
  EventsRequestBody,
  EventsEventFilter,
  CollapsedEventsResponse,
} from '../models/events.interface';

export interface EventsContentParams {
  collapse_on?: CollapseOnOption[];
  entity?: string[];
  entityType?: string;
  time?: string;
  timePeriod?: string;
  temporality?: EventTemporality[];
  factuality?: EventFactuality[];
  event_ids?: string[];
  lang?: string[];
  project_id?: string;
}

export interface EventsTimeSeriesResponse {
  events: [string, number][];
  columns: [string, string];
}

export interface EventsTimeSeriesDataPoint {
  date: string;
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class EventsUtilsService {
  constructor(
    private commonDataParams: CommonDataParamsService,
    private eventsService: EventsService,
  ) {}

  fetchEventsTimeSeries(
    params?: EventsContentParams,
    apiOverrides?: Partial<EventsRequestBody>,
  ): Observable<EventsTimeSeriesDataPoint[]> {
    const body = this.buildEventsRequestBody(
      {
        collapse_on: [
          'event_id',
          'factuality',
          'temporality',
          'role',
          'participant',
        ],
        ...params,
      },
      apiOverrides,
    );

    return this.eventsService.getEventsTimeSeries(body).pipe(
      map((response: CollapsedEventsResponse) =>
        this.transformTimeSeriesResponse(response),
      ),
      catchError(() => of([])),
    );
  }

  fetchEventsTimeSeriesMatrix(
    params: EventsContentParams,
    apiOverrides?: Partial<EventsRequestBody>,
  ): Observable<CollapsedEventsResponse> {
    const body = this.buildEventsRequestBody(params, apiOverrides);
    return this.eventsService
      .getEventsTimeSeries(body)
      .pipe(
        catchError(() =>
          of<CollapsedEventsResponse>({ events: [], columns: [] }),
        ),
      );
  }

  // `language_filter` is required by events (unlike score_ts) — defaults to
  // ['en','de'] when absent from params and URL.
  buildEventsRequestBody(
    params: EventsContentParams,
    apiOverrides?: Partial<EventsRequestBody>,
  ): EventsRequestBody {
    const queryParams = this.commonDataParams.getCurrentQueryParams();

    const body: EventsRequestBody = {
      time_range: this.commonDataParams.generateTimeRange({
        type: 'entities',
        time: params.time,
        timePeriod: params.timePeriod,
      }),
    };

    const eventFilter: EventsEventFilter = {};

    const participants = this.commonDataParams.getEventParticipants(
      { entity: params.entity, entityType: params.entityType },
      queryParams,
    );
    if (participants.length > 0) {
      eventFilter.event_participants = participants;
    }

    const eventIds = this.commonDataParams.getEventIds(
      queryParams,
      params.event_ids,
    );
    if (eventIds?.length) {
      eventFilter.event_ids = eventIds;
    }

    const temporality = this.commonDataParams.getTemporalityWire(
      queryParams.temporality,
      params.temporality,
    );
    if (temporality?.length) {
      eventFilter.temporality = temporality;
    }

    const factuality = this.resolveFactuality(params, queryParams.factuality);
    if (factuality?.length) {
      eventFilter.factuality = factuality;
    }

    if (Object.keys(eventFilter).length > 0) {
      body.event_filter = eventFilter;
    }

    if (params.collapse_on?.length) {
      body.collapse_on = params.collapse_on;
    }

    const lang = this.resolveLanguage(params, queryParams.lang);
    if (lang?.length) {
      body.language_filter = lang;
    }

    const project = params.project_id ?? this.commonDataParams.getProject();
    if (project) {
      body.project = project;
    }

    return { ...body, ...apiOverrides };
  }

  private transformTimeSeriesResponse(
    response: EventsTimeSeriesResponse | CollapsedEventsResponse,
  ): EventsTimeSeriesDataPoint[] {
    if (!response?.events || !Array.isArray(response.events)) {
      return [];
    }

    return response.events.map((row) => ({
      date: row[0] as string,
      count: row[1] as number,
    }));
  }

  private resolveFactuality(
    params: EventsContentParams,
    urlFactuality: string | undefined,
  ): EventFactuality[] | undefined {
    if (params.factuality) {
      return params.factuality;
    }
    if (urlFactuality) {
      return this.commonDataParams.getFactualityValues(
        urlFactuality,
      ) as EventFactuality[];
    }
    return undefined;
  }

  private resolveLanguage(
    params: EventsContentParams,
    urlLang: string | undefined,
  ): string[] {
    if (params.lang) {
      return params.lang;
    }
    if (urlLang && urlLang !== 'multi') {
      return urlLang.split(',');
    }
    return ['en', 'de'];
  }
}
